/**
 * Client portal home.
 *
 * Airy single-column layout (Decision #14). Hero usage card per
 * active non-internal contract — large mono reading, contract
 * info on the right, segmented progress bar with period rails. A
 * pair of secondary cards: open tickets preview + recent reports
 * with download links. Footer hint reminds clients they continue
 * tickets by email.
 *
 * The data layer is org-scoped already (contracts, tickets, reports
 * all filter by viewer.organizationId for client viewers), so this
 * page just composes existing hooks.
 */

import { Link } from 'react-router-dom';
import { Download, FileText } from 'lucide-react';

import type { Contract, ReportListItem, Ticket } from '@beacon/core';

import { Status } from '@/components/beacon/status.js';
import { Skeleton } from '@/components/ui/skeleton.js';
import { useContracts, useContractUsage } from '@/hooks/use-contracts.js';
import { useReports } from '@/hooks/use-reports.js';
import { useTickets } from '@/hooks/use-tickets.js';
import { useViewer } from '@/hooks/use-viewer.js';
import { cn } from '@/lib/cn.js';

const OPEN_TICKET_STATUSES = new Set([
  'new',
  'triaged',
  'in_progress',
  'awaiting_client',
]);

export function ClientHome() {
  const viewerQuery = useViewer();
  const contractsQuery = useContracts();
  const ticketsQuery = useTickets();
  const reportsQuery = useReports(['sent']);

  if (viewerQuery.isLoading) {
    return <ClientHomeSkeleton />;
  }
  const viewer = viewerQuery.data;
  if (!viewer) {
    return (
      <div className="p-8 text-ink-300">
        Not signed in.{' '}
        <Link to="/login" className="text-beacon-300 hover:underline">
          Go to sign-in
        </Link>
      </div>
    );
  }

  const contracts = (contractsQuery.data ?? []).filter(
    (c) => c.status === 'active' && c.type !== 'internal'
  );
  const openTickets = (ticketsQuery.data ?? [])
    .filter((t) => OPEN_TICKET_STATUSES.has(t.status))
    .slice(0, 3);
  const sentReports = (reportsQuery.data ?? []).slice(0, 3);
  const firstName = viewer.name.split(' ')[0];
  const monthLabel = new Date().toLocaleString('en-AU', {
    month: 'long',
    year: 'numeric',
  });

  return (
    <div className="h-full overflow-y-auto bg-ink-900">
      <div className="max-w-[720px] mx-auto px-8 pt-14 pb-20">
        <div className="mb-10">
          <div className="text-[12px] uppercase tracking-[0.12em] text-ink-300 mono">
            {monthLabel}
          </div>
          <h1 className="text-[32px] font-semibold tracking-tight text-ink-100 mt-1">
            Welcome back, {firstName}.
          </h1>
          <p className="text-[14px] text-ink-300 mt-2 leading-relaxed max-w-[560px]">
            Here's a quick look at your Conduct HQ support for this month. You
            can always reply to any email to continue a ticket — this portal is
            a read-only view.
          </p>
        </div>

        {contracts.length === 0 ? (
          <div className="bg-ink-800 border border-ink-600 rounded-[14px] p-7 mb-8 text-[13px] text-ink-300">
            No active contracts on file. Reach out to your account owner if you
            think this is wrong.
          </div>
        ) : (
          contracts.map((c) => <ContractUsageHero key={c.id} contract={c} />)
        )}

        <div className="grid grid-cols-2 gap-5 mb-10">
          <OpenTicketsCard tickets={openTickets} />
          <RecentReportsCard reports={sentReports} />
        </div>

        <div className="text-center text-[12px] text-ink-400 mono">
          Need to raise a new ticket? Just email{' '}
          <span className="text-ink-200">support@conducthq.com</span>.
        </div>
      </div>
    </div>
  );
}

/* ----------------------------------------------------------------
 * Hero usage card. SLA contracts get a current-month rolling window
 * (period start, today, period end captions on the rail). BAU shows
 * lifetime usage with no rail. Tone of the bar tracks 80%/100%
 * thresholds — beacon-amber under 80, warn-500 amber-yellow at 80,
 * danger-500 red over.
 * ---------------------------------------------------------------- */
function ContractUsageHero({ contract }: { contract: Contract }) {
  const usageQuery = useContractUsage(contract.id);
  const usage = usageQuery.data;
  if (!usage || usage.type === 'internal') return null;

  const used = usage.usedHours;
  const cap =
    usage.type === 'sla' ? usage.allocatedHours : usage.budgetHours;
  const remaining = usage.remainingHours;
  const percent = Math.min(100, Math.max(0, usage.percent));
  const tone =
    percent >= 100
      ? 'bg-danger-500'
      : percent >= 80
        ? 'bg-warn-500'
        : 'bg-beacon-400';

  const today = new Date();
  const periodLabel = usage.type === 'sla'
    ? new Date(usage.periodStartDate).toLocaleString('en-AU', {
        month: 'long',
        year: 'numeric',
      })
    : 'Lifetime';
  const daysToReset =
    usage.type === 'sla'
      ? Math.max(
          0,
          Math.ceil(
            (Date.parse(usage.periodEndDate) - today.getTime()) / 86_400_000
          )
        )
      : null;

  return (
    <div className="bg-ink-800 border border-ink-600 rounded-[14px] p-7 mb-8">
      <div className="flex items-start justify-between gap-4 mb-5">
        <div>
          <div className="text-[10.5px] uppercase tracking-[0.12em] text-ink-300 font-semibold">
            {usage.type === 'sla'
              ? `Your ${periodLabel} usage`
              : `${contract.name} — lifetime`}
          </div>
          <div className="mt-1.5 flex items-baseline gap-2">
            <span className="mono text-[44px] font-semibold leading-none text-ink-100">
              {used.toFixed(1)}
            </span>
            <span className="text-[16px] text-ink-300">
              of {cap} hours used
            </span>
          </div>
          <div className="text-[13px] text-ink-300 mt-2">
            {remaining >= 0
              ? `${remaining.toFixed(1)} hours remaining`
              : `${Math.abs(remaining).toFixed(1)} hours over`}
            {daysToReset != null && ` · period resets in ${daysToReset} days`}
          </div>
        </div>
        <div className="text-right shrink-0">
          <div className="text-[10.5px] uppercase tracking-[0.12em] text-ink-300 font-semibold">
            Contract
          </div>
          <div className="text-[13px] text-ink-100 mt-1">
            {contract.type.toUpperCase()}
            {usage.type === 'sla' && ` · ${cap}h / month`}
          </div>
          {contract.endDate && (
            <div className="text-[11.5px] text-ink-400 mono">
              Renews{' '}
              {new Date(contract.endDate).toLocaleString('en-AU', {
                day: '2-digit',
                month: 'short',
                year: 'numeric',
              })}
            </div>
          )}
        </div>
      </div>
      <div className="relative h-2.5 bg-ink-700 rounded-full overflow-hidden">
        <div
          className={cn('h-full rounded-full transition-all', tone)}
          style={{ width: `${percent}%` }}
        />
      </div>
      {usage.type === 'sla' && (
        <div className="flex justify-between text-[11.5px] text-ink-300 mono mt-2">
          <span>{formatShortDay(usage.periodStartDate)}</span>
          <span>
            Today · {formatShortDay(today.toISOString().slice(0, 10))}
          </span>
          <span>{formatShortDay(usage.periodEndDate)}</span>
        </div>
      )}
    </div>
  );
}

function OpenTicketsCard({ tickets }: { tickets: Ticket[] }) {
  return (
    <Link
      to="/tickets"
      className="bg-ink-800 border border-ink-600 rounded-[12px] p-5 hover:border-ink-500 transition-colors block focus-ring"
    >
      <div className="flex items-center justify-between mb-3">
        <div className="text-[10.5px] uppercase tracking-[0.12em] text-ink-300 font-semibold">
          Open tickets
        </div>
        <span className="mono text-[11.5px] text-ink-300 bg-ink-700 px-1.5 rounded">
          {tickets.length}
        </span>
      </div>
      {tickets.length === 0 ? (
        <p className="text-[12.5px] text-ink-400">
          No open tickets right now.
        </p>
      ) : (
        <div className="space-y-2.5">
          {tickets.map((t) => (
            <div key={t.id} className="border-t border-ink-600 pt-2.5">
              <div className="text-[11.5px] mono text-ink-300 mb-0.5">
                {t.number}
              </div>
              <div className="text-[13.5px] text-ink-100 leading-snug line-clamp-2">
                {t.subject}
              </div>
              <div className="mt-1.5">
                <Status s={t.status} />
              </div>
            </div>
          ))}
        </div>
      )}
    </Link>
  );
}

function RecentReportsCard({ reports }: { reports: ReportListItem[] }) {
  return (
    <div className="bg-ink-800 border border-ink-600 rounded-[12px] p-5">
      <div className="flex items-center justify-between mb-3">
        <div className="text-[10.5px] uppercase tracking-[0.12em] text-ink-300 font-semibold">
          Recent reports
        </div>
        <span className="mono text-[11.5px] text-ink-300 bg-ink-700 px-1.5 rounded">
          {reports.length}
        </span>
      </div>
      {reports.length === 0 ? (
        <p className="text-[12.5px] text-ink-400">No reports sent yet.</p>
      ) : (
        <div className="space-y-1">
          {reports.map((r) => (
            <ReportRow key={r.id} report={r} />
          ))}
        </div>
      )}
    </div>
  );
}

function ReportRow({ report }: { report: ReportListItem }) {
  const label = new Date(report.periodStartDate).toLocaleString('en-AU', {
    month: 'long',
    year: 'numeric',
  });
  const sub = report.sentAt
    ? `Sent ${new Date(report.sentAt).toLocaleString('en-AU', {
        day: '2-digit',
        month: 'short',
      })}`
    : '—';
  // Server gates client downloads to status='sent' and 404s otherwise.
  const downloadHref = `/api/reports/${encodeURIComponent(report.id)}/download`;
  return (
    <Link
      to={`/reports/${report.id}`}
      className="flex items-center gap-3 px-2 py-2 rounded-md hover:bg-ink-700 transition-colors focus-ring group"
    >
      <span className="w-8 h-10 rounded-[3px] bg-ink-700 border border-ink-500 flex items-center justify-center shrink-0">
        <FileText size={16} className="text-ink-300" />
      </span>
      <div className="flex-1 min-w-0">
        <div className="text-[13px] text-ink-100 truncate">{label}</div>
        <div className="text-[11.5px] text-ink-300 mono truncate">
          {report.contractName} · {sub}
        </div>
      </div>
      <a
        href={downloadHref}
        onClick={(e) => e.stopPropagation()}
        className="p-1 rounded text-ink-300 group-hover:text-ink-100 transition-colors"
        aria-label={`Download ${label} report`}
      >
        <Download size={14} />
      </a>
    </Link>
  );
}

function formatShortDay(iso: string): string {
  return new Date(iso).toLocaleString('en-AU', {
    day: '2-digit',
    month: 'short',
  });
}

/**
 * Portal home skeleton — airy, hero-shaped silhouette matching the
 * client portal aesthetic. Only triggered while the viewer query is
 * pending; sub-cards (contracts/tickets/reports) render their own
 * progressive states once the viewer is known.
 */
function ClientHomeSkeleton() {
  return (
    <div className="h-full overflow-y-auto bg-ink-900" aria-busy="true">
      <div className="max-w-[720px] mx-auto px-8 pt-14 pb-20">
        <div className="mb-10 space-y-2">
          <Skeleton className="h-3 w-32" />
          <Skeleton className="h-8 w-2/3 max-w-[420px]" />
          <Skeleton className="h-3 w-full max-w-[560px]" />
          <Skeleton className="h-3 w-3/4 max-w-[420px]" />
        </div>
        <div className="bg-ink-800 border border-ink-600 rounded-[14px] p-7 mb-8">
          <div className="flex items-start justify-between gap-4 mb-5">
            <div className="space-y-2">
              <Skeleton className="h-3 w-40" />
              <Skeleton className="h-10 w-48" />
              <Skeleton className="h-3 w-44" />
            </div>
            <div className="text-right space-y-2">
              <Skeleton className="h-3 w-20 ml-auto" />
              <Skeleton className="h-3 w-32 ml-auto" />
            </div>
          </div>
          <Skeleton className="h-2.5 w-full rounded-full" />
        </div>
        <div className="grid grid-cols-2 gap-5 mb-10">
          {Array.from({ length: 2 }).map((_, i) => (
            <div
              key={i}
              className="bg-ink-800 border border-ink-600 rounded-[12px] p-5 space-y-3"
            >
              <Skeleton className="h-3 w-24" />
              <Skeleton className="h-3 w-full" />
              <Skeleton className="h-3 w-2/3" />
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}
